'use client';

import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { BarChart3, TrendingUp, RefreshCw, Users, Crown, Clock, Camera, Sparkles } from 'lucide-react';

interface StatBlock {
  sent: number;
  total: number;
}

interface TechStats {
  today: StatBlock;
  week: StatBlock;
  allTime: StatBlock;
  avgSendMinutes: number | null;
  avgPhotos: number | null;
  enhanceRate: number;
  lastUpdated: string;
}

interface TeamMember {
  slug: string;
  name: string;
  stats: TechStats;
}

// null, not 0, when there was nothing to text. A tech whose only stop was
// excused used to open this tab to a red 0% and a full red bar.
function pct(block: StatBlock): number | null {
  if (!block || block.total === 0) return null;
  return Math.round((block.sent / block.total) * 100);
}

function getMotivation(p: number | null): string {
  if (p == null) return '';
  if (p === 100) return '🎯';
  if (p >= 90) return '💪';
  if (p >= 75) return '👍';
  if (p >= 50) return '🚀';
  if (p > 0) return '📈';
  return '';
}

function StatCard({ label, block }: { label: string; block: StatBlock }) {
  const p = pct(block);
  const noRate = p == null;
  return (
    <div className="bg-base-100 rounded-2xl p-5 shadow-sm border border-base-200">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-base-content/60 uppercase tracking-wide">{label}</span>
        <span className="text-sm">{getMotivation(p)}</span>
      </div>
      <div className="flex items-end gap-2 mb-3">
        <span className="text-3xl font-bold text-base-content">{block.sent}</span>
        <span className="text-base-content/40 text-lg mb-0.5">/ {block.total} service orders</span>
      </div>
      <div className="w-full bg-base-200 rounded-full h-2.5 mb-1.5">
        {!noRate && (
          <div
            className="h-2.5 rounded-full transition-all duration-500"
            style={{
              width: `${p}%`,
              backgroundColor: p === 100 ? '#01AA90' : (p as number) >= 75 ? '#D86018' : '#ef4444',
            }}
          />
        )}
      </div>
      <div className="text-right">
        <span className="text-xs font-medium text-base-content/50">
          {noRate ? 'Nothing to text — not scored' : `${p}% compliance`}
        </span>
      </div>
    </div>
  );
}

function MetricRow({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div className="flex items-center gap-3 py-3 border-b border-base-200 last:border-0">
      <div className="w-9 h-9 rounded-full bg-base-200 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-base-content">{label}</p>
        {sub && <p className="text-xs text-base-content/40">{sub}</p>}
      </div>
      <span className="text-base font-bold text-base-content">{value}</span>
    </div>
  );
}

function TeamRow({ member, rank }: { member: TeamMember; rank: number }) {
  const p = pct(member.stats.allTime);
  const wp = pct(member.stats.week);
  const fmt = (v: number | null) => v == null ? '—' : `${v}%`;
  return (
    <div className="flex items-center gap-3 py-3 border-b border-base-200 last:border-0">
      <div className="w-7 text-center">
        {rank === 1 ? <Crown className="w-5 h-5 text-yellow-500 mx-auto" /> :
         <span className="text-sm font-bold text-base-content/30">{rank}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-base-content truncate">{member.name}</p>
        <p className="text-xs text-base-content/40">
          This week: {member.stats.week.sent}/{member.stats.week.total} ({fmt(wp)})
          {member.stats.avgSendMinutes != null && (
            <> · ⚡ {member.stats.avgSendMinutes}m avg</>
          )}
        </p>
      </div>
      <div className="text-right">
        <p className="text-lg font-bold" style={{ color: p == null ? '#9ca3af' : p >= 90 ? '#01AA90' : p >= 75 ? '#D86018' : '#ef4444' }}>
          {fmt(p)}
        </p>
        <p className="text-[10px] text-base-content/30">
          {member.stats.allTime.sent}/{member.stats.allTime.total}
        </p>
      </div>
    </div>
  );
}

export default function StatsPage() {
  const { status } = useSession();
  const router = useRouter();
  const [stats, setStats] = useState<TechStats | null>(null);
  const [name, setName] = useState('');
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (status === 'unauthenticated') {
      router.push('/login');
    }
  }, [status, router]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, teamRes] = await Promise.all([
        fetch('/api/stats', { cache: 'no-store' }),
        fetch('/api/team-stats', { cache: 'no-store' }).catch(() => null),
      ]);

      const statsData = await statsRes.json();
      if (statsData.error && statsRes.status !== 200) {
        setError(statsData.error);
      } else {
        setStats(statsData.stats);
        setName(statsData.name || '');
      }

      if (teamRes && teamRes.ok) {
        const teamData = await teamRes.json();
        setTeam(teamData.team || []);
        setIsOwner(true);
      }
    } catch {
      setError('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (status === 'authenticated') {
      fetchData();
    }
  }, [status]);

  if (status === 'loading' || status === 'unauthenticated') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary to-primary/80 text-white px-5 pt-8 pb-6">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-6 h-6" />
              <h1 className="text-xl font-bold">My Stats</h1>
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="p-2 rounded-full hover:bg-white/20 transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {name && <p className="mt-1 text-white/80 text-sm">{name}</p>}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 -mt-3">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <span className="loading loading-spinner loading-lg text-primary" />
            <p className="text-base-content/50 text-sm">Loading stats...</p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-error text-sm">{error}</p>
          </div>
        ) : !stats ? (
          <div className="text-center py-16">
            <TrendingUp className="w-12 h-12 mx-auto text-base-content/20 mb-4" />
            <h2 className="text-lg font-semibold text-base-content/60 mb-2">No service data yet</h2>
            <p className="text-sm text-base-content/40">
              Complete your first service and your stats will appear here.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 pt-2">
            <StatCard label="Today" block={stats.today} />
            <StatCard label="This Week" block={stats.week} />
            <StatCard label="All Time" block={stats.allTime} />

            {/* Performance metrics */}
            <div className="bg-base-100 rounded-2xl shadow-sm border border-base-200 p-4">
              <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wide mb-1 px-1">
                Performance Insights
              </h3>
              {stats.avgSendMinutes != null && (
                <MetricRow
                  icon={<Clock className="w-4 h-4 text-primary" />}
                  label="Avg send time"
                  value={`${stats.avgSendMinutes} min`}
                  sub="after service completion"
                />
              )}
              {stats.avgPhotos != null && (
                <MetricRow
                  icon={<Camera className="w-4 h-4 text-secondary" style={{ color: '#D86018' }} />}
                  label="Avg photos"
                  value={`${stats.avgPhotos}`}
                  sub="per service"
                />
              )}
              <MetricRow
                icon={<Sparkles className="w-4 h-4" style={{ color: '#01AA90' }} />}
                label="AI enhanced"
                value={`${stats.enhanceRate}%`}
                sub="of submitted reports"
              />
            </div>

            {stats.lastUpdated && (
              <p className="text-center text-xs text-base-content/30 mt-1">
                Last updated: {new Date(stats.lastUpdated).toLocaleString()}
              </p>
            )}

            {/* Team Section — Owner Only */}
            {isOwner && team.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <Users className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-bold text-base-content">Team Leaderboard</h2>
                </div>
                <div className="bg-base-100 rounded-2xl shadow-sm border border-base-200 p-4">
                  {team.map((m, i) => (
                    <TeamRow key={m.slug} member={m} rank={i + 1} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
